import { Injectable, inject } from '@angular/core';
import { map, Observable } from 'rxjs';
import { SignInResponse } from '../responses/sign-in.response';
import { SignInRequest } from '../requests/sign-in.request';
import { SignUpAsClientRequest } from '../requests/sign-up-as-client.request';
import { SignUpAsClientResponse } from '../responses/sign-up-as-client.response';
import { MeResponse } from '../responses/me.response';
import { RefreshTokenResponse } from '../responses/refresh-token.response';
import { blank } from '@web/utils';
import { SignUpAsCompanyRequest } from '../requests/sign-up-as-company.request';
import { SignUpAsCompanyResponse } from '../responses/sign-up-as-company.response';
import { ActivateAccountRequest } from '../requests/activate-account.request';
import { RemindPasswordRequest } from '../requests/remind-password.request';
import { ResetPasswordRequest } from '../requests/reset-password.request';
import { ApiDependencyProviderService } from '@web/common';

@Injectable({
  providedIn: 'root',
})
export class AuthApiService {
  private readonly provider = inject(ApiDependencyProviderService);

  public signInByCredentials(
    request: SignInRequest,
  ): Observable<SignInResponse> {
    const path = 'auth/login';
    const url = this.provider.httpUrlFactory.simple(this.hostUrl(), path);
    const body = this.provider.httpHelper.requestToBody(request, SignInRequest);
    const withCredentials = true;
    const context = this.provider.httpContextBuilder
      .skipByRefreshTokenInterceptor()
      .skipByHttpErrorInterceptor()
      .skipByAuthTokenInterceptor()
      .build();

    return this.provider.http
      .post(url, body, { withCredentials, context })
      .pipe(map(this.provider.httpHelper.bodyToResponse(SignInResponse)));
  }

  public signUpAsClient(
    request: SignUpAsClientRequest,
  ): Observable<SignUpAsClientResponse> {
    const path = 'auth/user/register';
    const url = this.provider.httpUrlFactory.simple(this.hostUrl(), path);
    const body = this.provider.httpHelper.requestToBody(
      request,
      SignUpAsClientRequest,
    );
    const context = this.provider.httpContextBuilder
      .skipByRefreshTokenInterceptor()
      .skipByHttpErrorInterceptor()
      .skipByAuthTokenInterceptor()
      .build();

    return this.provider.http
      .post(url, body, { context })
      .pipe(
        map(this.provider.httpHelper.bodyToResponse(SignUpAsClientResponse)),
      );
  }

  public signUpAsCompany(
    request: SignUpAsCompanyRequest,
  ): Observable<SignUpAsCompanyResponse> {
    const path = 'auth/company/register';
    const url = this.provider.httpUrlFactory.simple(this.hostUrl(), path);
    const body = this.provider.httpHelper.requestToBody(
      request,
      SignUpAsCompanyRequest,
    );
    const context = this.provider.httpContextBuilder
      .skipByRefreshTokenInterceptor()
      .skipByHttpErrorInterceptor()
      .skipByAuthTokenInterceptor()
      .build();

    return this.provider.http
      .post(url, body, { context })
      .pipe(
        map(this.provider.httpHelper.bodyToResponse(SignUpAsClientResponse)),
      );
  }

  public me(): Observable<MeResponse> {
    const path = 'auth/me';
    const url = this.provider.httpUrlFactory.simple(this.hostUrl(), path);
    const context = this.provider.httpContextBuilder
      .skipByHttpErrorInterceptor()
      .build();

    return this.provider.http
      .get(url, { context })
      .pipe(map(this.provider.httpHelper.bodyToResponse(MeResponse)));
  }

  public refreshToken(): Observable<RefreshTokenResponse> {
    const path = 'auth/token/refresh';
    const url = this.provider.httpUrlFactory.simple(this.hostUrl(), path);
    const body = this.provider.httpHelper.emptyBody();
    const withCredentials = true;
    const context = this.provider.httpContextBuilder
      .skipByRefreshTokenInterceptor()
      .skipByAuthTokenInterceptor()
      .skipByHttpErrorInterceptor()
      .build();

    return this.provider.http
      .post(url, body, { withCredentials, context })
      .pipe(map(this.provider.httpHelper.bodyToResponse(RefreshTokenResponse)));
  }

  public signOut(): Observable<void> {
    const path = 'auth/token/revoke';
    const url = this.provider.httpUrlFactory.simple(this.hostUrl(), path);
    const withCredentials = true;
    const context = this.provider.httpContextBuilder
      .skipByRefreshTokenInterceptor()
      .skipByAuthTokenInterceptor()
      .skipByHttpErrorInterceptor()
      .build();

    return this.provider.http
      .delete(url, { withCredentials, context })
      .pipe(blank());
  }

  public activate(request: ActivateAccountRequest): Observable<void> {
    const path = 'auth/activate';
    const url = this.provider.httpUrlFactory.simple(this.hostUrl(), path);
    const body = this.provider.httpHelper.requestToBody(
      request,
      ActivateAccountRequest,
    );
    const context = this.provider.httpContextBuilder
      .skipByRefreshTokenInterceptor()
      .skipByAuthTokenInterceptor()
      .skipByHttpErrorInterceptor()
      .skipByLoadingInterceptor()
      .build();

    return this.provider.http.patch(url, body, { context }).pipe(blank());
  }

  public remindPassword(request: RemindPasswordRequest): Observable<void> {
    const path = 'auth/password/remind';
    const url = this.provider.httpUrlFactory.simple(this.hostUrl(), path);
    const body = this.provider.httpHelper.requestToBody(
      request,
      RemindPasswordRequest,
    );
    const context = this.provider.httpContextBuilder
      .skipByRefreshTokenInterceptor()
      .skipByAuthTokenInterceptor()
      .skipByHttpErrorInterceptor()
      .skipByLoadingInterceptor()
      .build();

    return this.provider.http.post(url, body, { context }).pipe(blank());
  }

  public resetPassword(request: ResetPasswordRequest): Observable<void> {
    const path = 'auth/password/reset';
    const url = this.provider.httpUrlFactory.simple(this.hostUrl(), path);
    const body = this.provider.httpHelper.requestToBody(
      request,
      ResetPasswordRequest,
    );
    const context = this.provider.httpContextBuilder
      .skipByRefreshTokenInterceptor()
      .skipByAuthTokenInterceptor()
      .skipByHttpErrorInterceptor()
      .skipByLoadingInterceptor()
      .build();

    return this.provider.http.patch(url, body, { context }).pipe(blank());
  }

  protected hostUrl(): string {
    return this.provider.environment.ApiHostUrl.SSO;
  }
}
